import {
  buildAcceptedMimeTypes,
  isFileTypeAllowed,
  toUploadedDocument,
} from '@/screens/ReportCreation/components/fields/FileUploaderField/fileUploaderField.utils';
import type { UploadedDocument } from '@/types/formField.types';
import { useAppTranslation } from '@hooks/useAppTranslation';
import * as DocumentPicker from 'expo-document-picker';
import { useState } from 'react';

const MAX_CSV_FILE_SIZE_IN_BYTES = 15 * 1024 * 1024;

interface UseCsvImportFilePickerResult {
  files: UploadedDocument[];
  feedbackMessage?: string;
  canImport: boolean;
  onUploadPress: () => Promise<void>;
  onRemoveFile: (fileId: string) => void;
}

export const useCsvFilePicker = (): UseCsvImportFilePickerResult => {
  const { t } = useAppTranslation();
  const [files, setFiles] = useState<UploadedDocument[]>([]);
  const [feedbackMessage, setFeedbackMessage] = useState<string | undefined>();

  const acceptedFormats = ['csv'];
  const acceptedMimeTypes = buildAcceptedMimeTypes(acceptedFormats);

  const onUploadPress = async (): Promise<void> => {
    setFeedbackMessage(undefined);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: false,
        type: acceptedMimeTypes,
        copyToCacheDirectory: true,
      });

      const asset = result.assets?.[0];
      if (result.canceled || !asset) {
        return;
      }

      const uploadedDocument = toUploadedDocument(asset);

      if (
        !isFileTypeAllowed(uploadedDocument, acceptedFormats, acceptedMimeTypes)
      ) {
        setFeedbackMessage(t('reportCreation.step4.errors.unsupportedFormat'));
        return;
      }

      if (
        uploadedDocument.size &&
        uploadedDocument.size > MAX_CSV_FILE_SIZE_IN_BYTES
      ) {
        setFeedbackMessage(t('reportCreation.step4.errors.sizeExceeded'));
        return;
      }

      // Only one CSV can be selected at a time.
      setFiles([uploadedDocument]);
    } catch (error) {
      console.error('CSV file picker failed', error);
    }
  };

  const onRemoveFile = (fileId: string): void => {
    setFiles((previousFiles) =>
      previousFiles.filter((file) => file.id !== fileId),
    );
    setFeedbackMessage(undefined);
  };

  return {
    files,
    feedbackMessage,
    canImport: files.length > 0,
    onUploadPress,
    onRemoveFile,
  };
};
